import { useEffect, useMemo, useRef, useState } from "react";
import InfoTooltip from "../../components/common/InfoTooltip";
import { SALON_SPEAK_TOOLTIPS } from "../../constants/salonSpeakTooltips";
import {
  canRecognizeSpeech,
  canRecordAudio,
  extractImportantWords,
  scoreAttempt,
  speakText,
  speakVietnameseText,
  startVoiceCapture,
} from "./speechUtils";

const LEVELS = [1, 2, 3, 4];

const getLevelTitle = (level) => `Level ${level}`;

function formatLastSavedLabel(isoUtc, salonTimezone) {
  if (!isoUtc) return "";
  const text = String(isoUtc).trim();
  if (!text) return "";
  const hasOffset = /([zZ]|[+-]\d{2}:\d{2})$/.test(text);
  const parsed = new Date(hasOffset ? text : `${text}Z`);
  if (Number.isNaN(parsed.getTime())) return "";
  const formatOptions = {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };
  try {
    if (salonTimezone) {
      return parsed.toLocaleString([], {
        ...formatOptions,
        timeZone: salonTimezone,
      });
    }
    return parsed.toLocaleString([], formatOptions);
  } catch (err) {
    return parsed.toLocaleString([], formatOptions);
  }
}

export default function SalonSpeakLessons({
  lessons,
  lessonProgress,
  salonTimezone,
  onSaveAttempt,
  onSaveProgress,
  onResetLevelProgress,
  onResetAllProgress,
  salonMode,
  onToggleSalonMode,
}) {
  const completedLessons = useMemo(() => {
    const completed = lessonProgress?.completed_lessons;
    if (!Array.isArray(completed)) return new Set();
    return new Set(completed.map((item) => String(item)));
  }, [lessonProgress]);

  const [activeLevel, setActiveLevel] = useState(1);
  const [selectedLessonId, setSelectedLessonId] = useState("");
  const [showTranslations, setShowTranslations] = useState(true);
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [markSaving, setMarkSaving] = useState(false);
  const [markNotice, setMarkNotice] = useState("");
  const [progressSaving, setProgressSaving] = useState(false);
  const [progressNotice, setProgressNotice] = useState("");
  const [progressError, setProgressError] = useState("");
  const [optimisticCompletedLessonIds, setOptimisticCompletedLessonIds] = useState(
    new Set()
  );
  const recorderRef = useRef(null);

  const lessonsByLevel = useMemo(() => {
    const grouped = {};
    LEVELS.forEach((level) => {
      grouped[level] = [];
    });
    (lessons || []).forEach((item) => {
      const level = Number(item?.level);
      if (!Number.isFinite(level)) return;
      if (!grouped[level]) grouped[level] = [];
      grouped[level].push(item);
    });
    return grouped;
  }, [lessons]);

  const effectiveCompletedLessons = useMemo(() => {
    const merged = new Set(completedLessons);
    optimisticCompletedLessonIds.forEach((id) => merged.add(id));
    return merged;
  }, [completedLessons, optimisticCompletedLessonIds]);

  const unlockedLevels = useMemo(() => {
    const ids = new Set([1]);
    LEVELS.forEach((level) => {
      if (level === 1) return;
      const prev = lessonsByLevel[level - 1] || [];
      const prevCompleted =
        prev.length > 0 && prev.every((lesson) => effectiveCompletedLessons.has(lesson.id));
      if (prevCompleted) {
        ids.add(level);
      }
    });
    return ids;
  }, [lessonsByLevel, effectiveCompletedLessons]);

  const levelLessons = useMemo(
    () => lessonsByLevel[activeLevel] || [],
    [lessonsByLevel, activeLevel]
  );

  const unlockedLessonIds = useMemo(() => {
    const ids = new Set();
    levelLessons.forEach((lesson, index) => {
      if (index === 0) {
        ids.add(lesson.id);
        return;
      }
      const prev = levelLessons[index - 1];
      if (effectiveCompletedLessons.has(prev.id)) {
        ids.add(lesson.id);
      }
    });
    return ids;
  }, [levelLessons, effectiveCompletedLessons]);

  useEffect(() => {
    if (activeLevel !== 1 && !unlockedLevels.has(activeLevel)) {
      setActiveLevel(1);
    }
  }, [activeLevel, unlockedLevels]);

  useEffect(() => {
    if (levelLessons.length === 0) {
      setSelectedLessonId("");
      return;
    }
    const alreadyValid = levelLessons.some((item) => item.id === selectedLessonId);
    if (alreadyValid) return;
    const next =
      levelLessons.find((item) => unlockedLessonIds.has(item.id)) || levelLessons[0];
    setSelectedLessonId(next?.id || "");
  }, [levelLessons, selectedLessonId, unlockedLessonIds]);

  const selectedLesson = useMemo(
    () => levelLessons.find((item) => item.id === selectedLessonId) || null,
    [levelLessons, selectedLessonId]
  );
  const isSelectedCompleted = Boolean(
    selectedLesson && effectiveCompletedLessons.has(selectedLesson.id)
  );
  const hasAnyCompletedLesson = effectiveCompletedLessons.size > 0;
  const lastSavedLabel = formatLastSavedLabel(
    lessonProgress?.last_saved_at_utc,
    salonTimezone
  );
  const canResetActiveLevel = levelLessons.some((lesson) =>
    effectiveCompletedLessons.has(lesson.id)
  );

  useEffect(() => {
    setOptimisticCompletedLessonIds((prev) => {
      if (!prev.size) return prev;
      const next = new Set();
      prev.forEach((lessonId) => {
        if (!completedLessons.has(lessonId)) {
          next.add(lessonId);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [completedLessons]);

  useEffect(() => {
    setMarkNotice("");
  }, [selectedLessonId]);

  useEffect(() => {
    setProgressNotice("");
    setProgressError("");
  }, [activeLevel]);

  const handleListen = async (line, rate, lang = "") => {
    let ok = false;
    if (String(lang || "").toLowerCase().startsWith("vi")) {
      ok = await speakVietnameseText(line, rate);
    } else {
      ok = lang ? await speakText(line, { rate, lang }) : await speakText(line, rate);
    }
    if (!ok) {
      setRecordError("Audio not supported on this browser.");
    }
  };

  const finishCapture = async ({ transcript, source }) => {
    if (!selectedLesson) return;
    const expectedText = (selectedLesson.conversation || [])
      .filter((line) => line.speaker === "tech")
      .map((line) => line.en)
      .join(" ");
    const importantWords = Array.from(
      new Set([
        ...(selectedLesson.keyWords || []),
        ...extractImportantWords(expectedText).slice(0, 12),
      ])
    );

    let scored = scoreAttempt({
      transcript: transcript || "",
      importantWords,
      attemptRecorded: true,
    });

    if (!transcript) {
      scored = {
        ...scored,
        total: Math.max(scored.total, 60),
        suggestions: [
          "Transcript unavailable. Self-check: did you say the key words clearly?",
          ...(scored.suggestions || []),
        ].slice(0, 4),
      };
    }

    setFeedback({
      transcript: transcript || "Transcript unavailable",
      source,
      ...scored,
    });

    try {
      await onSaveAttempt?.({
        attempt_type: "lesson",
        content_id: selectedLesson.id,
        transcript,
        score: scored.total,
        keywords_hit: scored.keywordsHit,
      });
      setOptimisticCompletedLessonIds((prev) => {
        const next = new Set(prev);
        next.add(selectedLesson.id);
        return next;
      });
    } catch (err) {
      setRecordError("Attempt saved locally but failed to sync. Please retry.");
    }
  };

  const handleRecord = async () => {
    if (!selectedLesson || recording) return;
    setRecordError("");
    setFeedback(null);
    setRecording(true);
    try {
      const controller = await startVoiceCapture({
        salonMode,
        onTranscript: () => null,
        onComplete: (result) => {
          setRecording(false);
          finishCapture(result);
        },
        onError: (reason) => {
          setRecordError(reason || "Recording failed");
        },
      });
      recorderRef.current = controller;
    } catch (err) {
      setRecording(false);
      setRecordError(
        "Recording is not available on this device. You can still continue with self-check."
      );
      if (!canRecognizeSpeech() && !canRecordAudio()) {
        await finishCapture({ transcript: null, source: "unsupported" });
      }
    }
  };

  const stopRecord = () => {
    recorderRef.current?.stop?.();
    recorderRef.current = null;
  };

  const handleMarkComplete = async () => {
    if (!selectedLesson || recording || markSaving || isSelectedCompleted) return;
    setRecordError("");
    setMarkNotice("");
    setMarkSaving(true);
    try {
      await onSaveAttempt?.({
        attempt_type: "lesson",
        content_id: selectedLesson.id,
        transcript: null,
        score: 15,
        keywords_hit: [],
      });
      setOptimisticCompletedLessonIds((prev) => {
        const next = new Set(prev);
        next.add(selectedLesson.id);
        return next;
      });
      setMarkNotice("Lesson marked complete.");
      setFeedback((prev) => {
        if (prev) return prev;
        return {
          transcript: "Marked complete without recording.",
          source: "manual",
          total: 15,
          transcriptScore: 0,
          politenessScore: 0,
          completionScore: 15,
          suggestions: [
            "You can still record anytime to practice speaking and improve your score.",
          ],
        };
      });
    } catch (err) {
      setRecordError("Unable to mark complete right now. Please try again.");
    } finally {
      setMarkSaving(false);
    }
  };

  const handleSaveProgressClick = async () => {
    if (progressSaving) return;
    setProgressSaving(true);
    setProgressNotice("");
    setProgressError("");
    try {
      await onSaveProgress?.();
      setProgressNotice("Progress saved.");
    } catch (err) {
      setProgressError("Unable to save progress right now. Please try again.");
    } finally {
      setProgressSaving(false);
    }
  };

  const handleResetLevelClick = async () => {
    if (progressSaving || !canResetActiveLevel) return;
    const confirmed = window.confirm(
      `Reset ${getLevelTitle(activeLevel)} progress? This will lock its lessons again.`
    );
    if (!confirmed) return;

    setProgressSaving(true);
    setProgressNotice("");
    setProgressError("");
    try {
      await onResetLevelProgress?.(activeLevel);
      setOptimisticCompletedLessonIds(new Set());
      setProgressNotice(`${getLevelTitle(activeLevel)} has been reset.`);
    } catch (err) {
      setProgressError("Unable to reset this level right now. Please try again.");
    } finally {
      setProgressSaving(false);
    }
  };

  const handleResetAllClick = async () => {
    if (progressSaving || !hasAnyCompletedLesson) return;
    const confirmed = window.confirm(
      "Reset all lesson progress? This will lock all levels after Level 1."
    );
    if (!confirmed) return;

    setProgressSaving(true);
    setProgressNotice("");
    setProgressError("");
    try {
      await onResetAllProgress?.();
      setOptimisticCompletedLessonIds(new Set());
      setProgressNotice("All levels have been reset.");
    } catch (err) {
      setProgressError("Unable to reset all levels right now. Please try again.");
    } finally {
      setProgressSaving(false);
    }
  };

  return (
    <div className="space-y-4 pb-2">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 font-semibold">
            <span>Levels</span>
            <InfoTooltip
              text={SALON_SPEAK_TOOLTIPS.lessons.levelUnlock}
              label="Level unlock rule"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSaveProgressClick}
              disabled={progressSaving}
              className="rounded-full border border-emerald-300 px-3 py-1.5 text-xs font-semibold text-emerald-700 disabled:opacity-60"
            >
              {progressSaving ? "Saving..." : "Save Progress"}
            </button>
            {lastSavedLabel ? (
              <span className="text-[11px] text-slate-500">Last saved {lastSavedLabel}</span>
            ) : null}
            <button
              type="button"
              onClick={handleResetLevelClick}
              disabled={progressSaving || !canResetActiveLevel}
              className="rounded-full border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-700 disabled:opacity-60"
            >
              Reset Level
            </button>
            <button
              type="button"
              onClick={handleResetAllClick}
              disabled={progressSaving || !hasAnyCompletedLesson}
              className="rounded-full border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-700 disabled:opacity-60"
            >
              Reset All Levels
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {LEVELS.map((level) => {
            const hasContent = (lessonsByLevel[level] || []).length > 0;
            const isUnlocked = unlockedLevels.has(level);
            const isLocked = level !== 1 && !isUnlocked;
            return (
              <button
                key={level}
                type="button"
                onClick={() => setActiveLevel(level)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  activeLevel === level
                    ? "bg-slate-900 text-white"
                    : "border border-slate-300 text-slate-700"
                }`}
                disabled={isLocked}
              >
                {getLevelTitle(level)}{" "}
                {isLocked ? "(Locked)" : !hasContent && level !== 1 ? "(Coming soon)" : ""}
              </button>
            );
          })}
        </div>
        {progressNotice ? (
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {progressNotice}
          </div>
        ) : null}
        {progressError ? (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            {progressError}
          </div>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
            Lessons
          </div>
          <div className="divide-y divide-slate-200">
            {levelLessons.length === 0 ? (
              <div className="px-4 py-4 text-sm text-slate-500">Coming soon.</div>
            ) : (
              levelLessons.map((lesson) => {
                const isLocked = !unlockedLessonIds.has(lesson.id);
                const isCompleted = effectiveCompletedLessons.has(lesson.id);
                const isSelected = selectedLessonId === lesson.id;
                return (
                  <button
                    key={lesson.id}
                    type="button"
                    onClick={() => !isLocked && setSelectedLessonId(lesson.id)}
                    className={`w-full px-4 py-3 text-left ${
                      isSelected ? "bg-rose-50" : "hover:bg-slate-50"
                    } ${isLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                    disabled={isLocked}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900">{lesson.title}</div>
                      <span className="text-[10px] font-semibold uppercase text-slate-500">
                        {isLocked ? "Locked" : isCompleted ? "Done" : "Open"}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{lesson.description}</div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
          {selectedLesson ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-slate-900">{selectedLesson.title}</h3>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <span>Translation</span>
                  <InfoTooltip
                    text={SALON_SPEAK_TOOLTIPS.lessons.translation}
                    label="Translation"
                  />
                  <input
                    type="checkbox"
                    checked={showTranslations}
                    onChange={(event) => setShowTranslations(event.target.checked)}
                    className="h-4 w-4"
                  />
                </label>
              </div>

              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 text-xs uppercase tracking-wide text-slate-500">
                  Model Conversation
                </div>
                <div className="divide-y divide-slate-200">
                  {(selectedLesson.conversation || []).map((line, idx) => (
                    <div key={`${line.speaker}-${idx}`} className="px-3 py-3 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] uppercase font-semibold text-slate-500">
                          {line.speaker === "tech" ? "Technician" : "Client"}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleListen(line.en, 0.75)}
                            className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                          >
                            Slow
                          </button>
                          <button
                            type="button"
                            onClick={() => handleListen(line.en, 1)}
                            className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                          >
                            Normal
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-slate-800">{line.en}</div>
                      {showTranslations ? (
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 text-xs text-slate-500">{line.vi}</div>
                          {line.vi ? (
                            <div className="shrink-0 flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleListen(line.vi, 0.75, "vi-VN")}
                                className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                              >
                                VN Slow
                              </button>
                              <button
                                type="button"
                                onClick={() => handleListen(line.vi, 1, "vi-VN")}
                                className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                              >
                                VN Normal
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Phrase Breakdown
                  </div>
                  <ul className="mt-2 space-y-2 text-sm text-slate-700 list-disc pl-4">
                    {(selectedLesson.phraseBreakdown || []).map((phrase, idx) => (
                      <li key={`phrase-${idx}`}>
                        <span className="font-semibold text-slate-900">{phrase.en}</span>
                        {showTranslations ? ` - ${phrase.vi}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Key Pronunciation Words
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(selectedLesson.keyWords || []).map((word) => (
                      <span
                        key={word}
                        className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700"
                      >
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                    Record Practice
                    <InfoTooltip text={SALON_SPEAK_TOOLTIPS.lessons.record} label="Record" />
                    <InfoTooltip text={SALON_SPEAK_TOOLTIPS.lessons.speed} label="Slow / Normal" />
                    <InfoTooltip
                      text={SALON_SPEAK_TOOLTIPS.lessons.markComplete}
                      label="Mark Complete"
                    />
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                    <span>Salon mode</span>
                    <InfoTooltip
                      text={SALON_SPEAK_TOOLTIPS.roleplay.salonMode}
                      label="Salon mode"
                    />
                    <input
                      type="checkbox"
                      checked={Boolean(salonMode)}
                      onChange={(event) => onToggleSalonMode?.(event.target.checked)}
                      className="h-4 w-4"
                    />
                  </label>
                </div>

                <div className="flex flex-col items-center gap-2">
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={recording ? stopRecord : handleRecord}
                      className={`h-14 min-w-[190px] rounded-full px-6 text-base font-semibold text-white ${
                        recording ? "bg-amber-500" : "bg-rose-500"
                      }`}
                    >
                      {recording ? "Stop Recording" : "Record"}
                    </button>
                    <button
                      type="button"
                      onClick={handleMarkComplete}
                      disabled={recording || markSaving || isSelectedCompleted}
                      className="h-12 rounded-full border border-slate-300 px-5 text-sm font-semibold text-slate-700 disabled:opacity-60"
                    >
                      {isSelectedCompleted
                        ? "Completed"
                        : markSaving
                        ? "Saving..."
                        : "Mark Complete"}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    {recording
                      ? "Listening... speak clearly now."
                      : "Tip: speak closer to microphone in noisy salon conditions."}
                  </p>
                </div>

                {markNotice ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    {markNotice}
                  </div>
                ) : null}

                {recordError ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                    {recordError}
                  </div>
                ) : null}

                {feedback ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm space-y-2">
                    <div className="font-semibold text-emerald-800">Feedback</div>
                    <div className="text-slate-700">
                      <span className="font-semibold">Transcript:</span> {feedback.transcript}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs font-semibold">
                      <span className="rounded-full bg-white px-2 py-1 text-slate-700">
                        Score: {feedback.total}/100
                      </span>
                      <span className="rounded-full bg-white px-2 py-1 text-slate-700">
                        Keyword match: {feedback.transcriptScore}
                      </span>
                      <span className="rounded-full bg-white px-2 py-1 text-slate-700">
                        Politeness: {feedback.politenessScore}
                      </span>
                    </div>
                    {feedback.suggestions?.length ? (
                      <ul className="list-disc pl-4 text-slate-700">
                        {feedback.suggestions.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-slate-700">Great clarity. Keep this pattern.</div>
                    )}
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-500">Select a lesson to begin.</div>
          )}
        </div>
      </div>
    </div>
  );
}
